import { DateTime } from "luxon";
import { useEffect, useRef, useState, type FunctionComponent } from "react";
import { useParams } from "react-router-dom";
import {
  type IComment,
  useGetProposalSocialCommentsAdminHttp,
} from "../../../../hooks/http/admin/proposal-creator-comments/get-proposal-social-comments.admin.http";
import { handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import Spinner from "../../../../components/spinner.components";
import { Separator } from "../../../../components/ui/separator";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import { Check } from "lucide-react";
import { useAddProposalSocialCommentsAdminHttp } from "../../../../hooks/http/admin/proposal-creator-comments/add-proposal-social-comments.admin.http";
import useProposalSocialSearchStore from "../../../../store/proposal-social-search-store";
import useProposalSocialStore from "../../../../store/proposal-creators.store";
import { useUserStore } from "../../../../store/user.store";

export const CommentsAdminSliderPartial: FunctionComponent = () => {
  const { proposalSocialId, proposalId } = useParams();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [comments, setComments] = useState<Array<IComment>>([]);
  const { user } = useUserStore();
  const {
    execute: getProposalSocialCommentsHttp,
    fetching: fetchingProposalSocialComments,
  } = useGetProposalSocialCommentsAdminHttp();
  const { toast } = useToast();

  const {
    updateCommentCount: updateCommentCountSearch,
    markCommentsRead: markCommentsReadSearch,
  } = useProposalSocialSearchStore();
  const { updateCommentCount, markCommentsRead } = useProposalSocialStore();

  const [message, setMessage] = useState("");

  const {
    execute: addProposalSocialCommentHttp,
    fetching: fetchingAddProposalSocialComment,
  } = useAddProposalSocialCommentsAdminHttp();

  async function addProposalSocialComment() {
    if (!proposalSocialId || !proposalId) {
      return;
    }
    if (!message.trim()) {
      return;
    }
    const response = await addProposalSocialCommentHttp({
      body: {
        message,
      },
      pathParams: { proposalId, proposalSocialId },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setComments([...comments, response.data.comment]);
    updateCommentCount(proposalSocialId, comments.length + 1);
    updateCommentCountSearch(proposalSocialId, comments.length + 1);
    markCommentsRead(proposalSocialId, user?.id || "");
    markCommentsReadSearch(proposalSocialId, user?.id || "");
    setMessage("");
    textareaRef.current?.focus();
    textareaRef.current?.blur();
    getProposalSocialComments();
  }

  async function getProposalSocialComments() {
    if (!proposalSocialId || !proposalId) {
      return;
    }
    const response = await getProposalSocialCommentsHttp({
      body: null,
      pathParams: { proposalId, proposalSocialId },
      queryParams: {
        markRead: true,
      },
    });
    if (!response.success) {
      return handleHttpError(response, toast);
    }
    setComments(response.data.comments);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getProposalSocialComments();
  }, [proposalSocialId, proposalId]);

  if (fetchingProposalSocialComments) {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <Spinner width={20} />
      </div>
    );
  }

  return (
    <div className="relative flex flex-col w-full h-full">
      <div className="top-0 sticky flex items-center gap-5 mb-6 px-4 w-full">
        <p className="font-semibold text-xl">Comments [{comments.length}]</p>
      </div>

      <div className="flex flex-col flex-1 py-5 overflow-y-auto">
        {comments.map((c) => {
          const userName = c.addedByUser.name;

          // optional: deterministic bg from first letter
          const initial = userName?.charAt(0).toUpperCase();

          return (
            <div key={c.id} className="flex flex-col px-4">
              {/* Header */}
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="flex justify-center items-center rounded-full w-7 h-7 font-semibold text-[10px] uppercase">
                    {initial}
                  </div>
                  <span className="font-medium text-sm">{userName}</span>
                  {
                    <span className="text-secondary text-xs">
                      {DateTime.fromISO(c.createdAt.toString()).toRelative()}
                    </span>
                  }
                </div>
              </div>
              <div className="mt-4 text-sm leading-6 whitespace-pre-wrap">
                {c.message}
              </div>

              {/* skip for last */}
              {c.id !== comments[comments.length - 1].id && (
                <Separator className="my-8" />
              )}
            </div>
          );
        })}

        {!comments.length && (
          <div className="flex flex-1 justify-center items-center text-center text-secondary text-sm">
            No comments yet.
          </div>
        )}
      </div>
      <div className="relative sticky-bottom-0">
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="px-6 pt-5 border-r-0 border-b-0 border-border border-l-0 rounded-0 h-24 font-semibold placeholder:text-secondary bordr resize-none"
          placeholder="Drop a comment..."
        />
        <Button
          onClick={addProposalSocialComment}
          className="right-2 bottom-2 absolute bg-primarybuttonbg text-black"
        >
          {fetchingAddProposalSocialComment ? (
            <Spinner width={15} />
          ) : (
            <Check className="w-4 h-4 text-black" />
          )}
        </Button>
      </div>
    </div>
  );
};
